import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'rounded-lg bg-primary-500 text-white hover:bg-primary-600',
        destructive: 'rounded-lg bg-red-600 text-white hover:bg-red-700',
        outline: 'rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
        secondary:
          'rounded-lg border border-secondary-400 bg-secondary-400/10 text-secondary-600 hover:bg-secondary-400/20',
        ghost: 'rounded-lg hover:bg-gray-100 text-gray-700',
        link: 'text-primary-500 underline-offset-4 hover:underline',
        /** Uiverse comic-button (Gautammsharma) — chunky shadow CTA. */
        comic:
          'rounded-[10px] border-2 border-black bg-[#ff5252] font-bold text-white shadow-[5px_5px_0_#000] hover:border-[#ff5252] hover:bg-white hover:text-[#ff5252] hover:shadow-[5px_5px_0_#ff5252] active:translate-y-1 active:border-black active:bg-[#fcf414] active:text-black active:shadow-none',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-lg px-3',
        lg: 'h-12 rounded-lg px-8 text-lg',
        icon: 'h-10 w-10',
        comic: 'h-auto px-5 py-2.5 text-xl md:text-2xl',
      },
    },
    compoundVariants: [
      { variant: 'comic', size: 'default', class: 'h-auto px-5 py-2.5 text-base md:text-lg' },
      { variant: 'comic', size: 'sm', class: 'h-auto rounded-[10px] px-3 py-1.5 text-sm shadow-[3px_3px_0_#000] hover:shadow-[3px_3px_0_#ff5252] active:translate-y-0.5' },
      { variant: 'comic', size: 'lg', class: 'h-auto px-6 py-3 text-xl md:text-2xl' },
      { variant: 'comic', size: 'comic', class: 'h-auto px-5 py-2.5 text-xl md:text-2xl' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
